import React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import { Link } from "react-router-dom";
import rehypeMathjax from "rehype-mathjax/svg";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

const remarkMathOptions = {
  singleDollarTextMath: true,
};

const rehypeMathjaxOptions = {
  tex: {
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"],
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"],
    ],
    processEscapes: true,
  },
};

/** App-relative, as opposed to protocol-relative `//host` or absolute URLs. */
const isAppRelative = (href: string) => href.startsWith("/") && !href.startsWith("//");

const components: Components = {
  // Internal links navigate the SPA in place: a new tab would lose the app
  // state, and the packaged Electron shell cannot open one at all.
  a: ({ children, href }) =>
    href && isAppRelative(href) ? (
      <Link to={href} className="text-primary underline">
        {children}
      </Link>
    ) : (
      <a href={href} target="_blank" rel="noreferrer" className="text-primary underline">
        {children}
      </a>
    ),
  code: ({ children, className }) => {
    const isBlock = /language-\w+/.test(className ?? "");
    if (isBlock) {
      return <code className={`${className ?? ""} block font-mono text-[12px]`}>{children}</code>;
    }
    return (
      <code className="rounded bg-surface-strong px-1 py-0.5 font-mono text-[12px]">
        {children}
      </code>
    );
  },
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-outline-strong pl-3 text-text-mid">
      {children}
    </blockquote>
  ),
  h1: ({ children }) => <h1 className="pt-1 text-base font-semibold text-text">{children}</h1>,
  h2: ({ children }) => <h2 className="pt-1 text-[15px] font-semibold text-text">{children}</h2>,
  h3: ({ children }) => <h3 className="pt-1 text-sm font-semibold text-text">{children}</h3>,
  h4: ({ children }) => <h4 className="pt-1 text-sm font-semibold text-text">{children}</h4>,
  h5: ({ children }) => <h5 className="pt-1 text-sm font-semibold text-text">{children}</h5>,
  h6: ({ children }) => <h6 className="pt-1 text-sm font-semibold text-text">{children}</h6>,
  li: ({ children }) => <li className="pl-1">{children}</li>,
  ol: ({ children }) => <ol className="list-decimal space-y-1 pl-5">{children}</ol>,
  p: ({ children }) => <p className="whitespace-pre-wrap">{children}</p>,
  pre: ({ children }) => (
    <pre className="overflow-x-auto whitespace-pre rounded-lg bg-surface-strong p-2">
      {children}
    </pre>
  ),
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  table: ({ children }) => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  td: ({ children }) => (
    <td className="border border-outline px-2 py-1 text-left align-top">{children}</td>
  ),
  th: ({ children }) => (
    <th className="border border-outline bg-surface-strong px-2 py-1 text-left align-top font-semibold">
      {children}
    </th>
  ),
  ul: ({ children }) => <ul className="list-disc space-y-1 pl-5">{children}</ul>,
};

export const ChatMarkdown = ({ content }: { content: string }) => (
  <div className="space-y-2 text-text">
    <ReactMarkdown
      remarkPlugins={[remarkGfm, [remarkMath, remarkMathOptions]]}
      rehypePlugins={[[rehypeMathjax, rehypeMathjaxOptions]]}
      components={components}
    >
      {content}
    </ReactMarkdown>
  </div>
);
